import React, { useState } from 'react';
import {
  MDBBtn, MDBBtnGroup, MDBModal, MDBModalBody, MDBModalFooter,
  MDBModalHeader, MDBIcon,
} from 'mdbreact';
import { PlatformIcon } from 'helpers/postDisplay';
import { parsePostFailures, parsePostSuccesses, parsePostSkipped } from 'helpers/postFailures';
import { retryPost } from 'api/posts/retryPost';
import { unpublishPost } from 'api/posts/unpublishPost';
import { canUnpublish } from 'helpers/postUnpublishable';
import addNotification from 'helpers/notify';

// Popup surfaced by the StatusBadge when a post ended in any failure
// state (posting-failed / partially-posted / email-failed /
// image-generation-failed). Shows a per-platform breakdown plus a
// single "Retry all platforms" button that re-emits POST_APPROVED on
// the worker side. The worker skips platforms already in
// postingResults.success, so partially-posted retries don't duplicate
// the platforms that already went out.

function headerForStatus(status) {
  switch (status) {
    case 'posting-failed':           return 'Posting failed';
    case 'partially-posted':         return 'Some platforms failed';
    case 'email-failed':             return 'Approval email failed';
    case 'image-generation-failed':  return 'Image generation failed';
    case 'posted':                   return 'Published';
    case 'unpublished':              return 'Taken down';
    default:                         return 'Post failure details';
  }
}

const PostFailureModal = ({ isOpen, toggle, post }) => {
  const [retrying, setRetrying] = useState(false);
  // null when idle; otherwise the platform being taken down, or 'all'.
  const [unpublishing, setUnpublishing] = useState(null);
  if (!post) return null;
  const failures  = parsePostFailures(post);
  const successes = parsePostSuccesses(post);
  const skipped   = parsePostSkipped(post);
  const status    = String(post.queueStatus || '');
  // image-/email- failed posts don't go through socialPostingProcessor,
  // so retrying via the social-posting path makes no sense for them.
  const canRetry  = status === 'posting-failed' || status === 'partially-posted';
  const showUnpublish = canUnpublish(post);
  const busy = retrying || unpublishing !== null;

  // Take a published post down — per platform (pass platform) or all (null).
  const onUnpublish = async (platform = null) => {
    setUnpublishing(platform || 'all');
    try {
      const res = await unpublishPost(post._id, platform);
      if (res && res.err) {
        addNotification(res.err, 'danger');
      } else {
        addNotification(
          platform
            ? `Taking the ${platform} post down…`
            : 'Taking the post down on all platforms…',
          'success'
        );
        toggle();
      }
    } catch {
      addNotification('Take-down request failed; please try again or contact support', 'danger');
    } finally {
      setUnpublishing(null);
    }
  };

  const onRetry = async () => {
    setRetrying(true);
    try {
      const res = await retryPost(post._id);
      if (res && res.err) {
        addNotification(res.err, 'danger');
      } else {
        addNotification('Retry queued — the worker will re-attempt on every failed platform', 'success');
        toggle();
      }
    } catch {
      addNotification('Retry request failed; please try again or contact support', 'danger');
    } finally {
      setRetrying(false);
    }
  };

  return (
    <MDBModal isOpen={isOpen} toggle={toggle} centered>
      <MDBModalHeader toggle={toggle}>{headerForStatus(status)}</MDBModalHeader>
      <MDBModalBody>
        {successes.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontWeight: 600, marginBottom: 6, color: '#15803d' }}>
              Published on
            </div>
            {successes.map((s, i) => (
              <div key={`s-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <PlatformIcon platform={s.platform} size={18} />
                <span style={{ textTransform: 'capitalize' }}>{s.platform}</span>
                {s.url && (
                  <a href={s.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, marginLeft: 6 }}>
                    View <MDBIcon icon="external-link-alt" size="sm" />
                  </a>
                )}
                {/* Per-platform take-down: delete this platform's share,
                    leave the rest live. */}
                <button
                  type="button"
                  onClick={() => onUnpublish(s.platform)}
                  disabled={busy}
                  title={`Take the ${s.platform} post down`}
                  style={{
                    marginLeft: 'auto', border: 'none', background: 'transparent',
                    color: '#b91c1c', fontSize: 12, fontWeight: 600,
                    cursor: busy ? 'not-allowed' : 'pointer', padding: '2px 6px',
                  }}>
                  <MDBIcon icon="trash-alt" size="sm" />{' '}
                  {unpublishing === s.platform ? 'Taking down…' : 'Take down'}
                </button>
              </div>
            ))}
          </div>
        )}

        <div style={{ fontWeight: 600, marginBottom: 6, color: '#b91c1c' }}>
          {failures.length === 1 ? 'Failure reason' : 'Failure reasons'}
        </div>
        {failures.length === 0 ? (
          <p style={{ color: '#525252' }}>No specific failure detail recorded for this post.</p>
        ) : (
          failures.map((f, i) => (
            <div key={`f-${i}`} style={{ display: 'flex', gap: 10, marginBottom: 10, alignItems: 'flex-start' }}>
              {/* Email- / image-generation-failed don't carry a real
                  platform — show a label instead of an icon. */}
              {f.label ? (
                <div style={{ minWidth: 90, fontWeight: 600, color: '#525252' }}>{f.label}</div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 90 }}>
                  <PlatformIcon platform={f.platform} size={18} />
                  <span style={{ textTransform: 'capitalize', fontWeight: 600 }}>{f.platform}</span>
                </div>
              )}
              <div style={{ flex: 1, color: '#374151', wordBreak: 'break-word' }}>
                {f.message}
              </div>
            </div>
          ))
        )}

        {skipped.length > 0 && (
          <div style={{ marginTop: 14 }}>
            <div style={{ fontWeight: 600, marginBottom: 6, color: '#92400e' }}>
              Skipped (not attempted)
            </div>
            {skipped.map((s, i) => (
              <div key={`k-${i}`} style={{ display: 'flex', gap: 10, marginBottom: 8, alignItems: 'flex-start' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 90 }}>
                  <PlatformIcon platform={s.platform} size={18} />
                  <span style={{ textTransform: 'capitalize', fontWeight: 600 }}>{s.platform}</span>
                </div>
                <div style={{ flex: 1, color: '#374151', wordBreak: 'break-word' }}>{s.message}</div>
              </div>
            ))}
          </div>
        )}
      </MDBModalBody>
      <MDBModalFooter>
        <MDBBtnGroup size="md">
          {canRetry && (
            <MDBBtn onClick={onRetry} disabled={busy} color="primary">
              {retrying ? 'Retrying…' : 'Retry all platforms'}
            </MDBBtn>
          )}
          {showUnpublish && successes.length > 1 && (
            <MDBBtn onClick={() => onUnpublish(null)} disabled={busy} color="danger">
              {unpublishing === 'all' ? 'Taking down…' : 'Take down all'}
            </MDBBtn>
          )}
          <MDBBtn onClick={toggle} disabled={busy} color="">
            Close
          </MDBBtn>
        </MDBBtnGroup>
      </MDBModalFooter>
    </MDBModal>
  );
};

export default PostFailureModal;
